// src/components/AITicTacToe.jsx
import React, { useEffect, useState } from "react";
import {
  X as IconX,
  Circle as IconO,
  Play,
  RefreshCw,
  BrainCircuit,
  Smile,
  Gauge,
  Gamepad,
} from "lucide-react";

const AITicTacToe = () => {
  const emptyBoard = Array(9).fill(null);
  const [board, setBoard] = useState(emptyBoard);
  const [isPlayerTurn, setIsPlayerTurn] = useState(true);
  const [winner, setWinner] = useState(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [difficulty, setDifficulty] = useState("medium"); // easy | medium | hard
  const [error, setError] = useState(null);
  const [winningLine, setWinningLine] = useState([]);

  const lines = [
    [0, 1, 2],
    [3, 4, 5],
    [6, 7, 8],
    [0, 3, 6],
    [1, 4, 7],
    [2, 5, 8],
    [0, 4, 8],
    [2, 4, 6],
  ];

  const getWinningLine = (b) => {
    for (let line of lines) {
      const [a, b1, c] = line;
      if (b[a] && b[a] === b[b1] && b[a] === b[c]) return line;
    }
    return [];
  };

  const checkWinner = (b) => {
    for (let [a, b1, c] of lines) {
      if (b[a] && b[a] === b[b1] && b[a] === b[c]) return b[a];
    }
    return b.includes(null) ? null : "Draw";
  };

  // Minimax (Hard)
  const minimax = (boardState, isMaximizing) => {
    const result = checkWinner(boardState);
    if (result !== null) {
      if (result === "O") return 1;
      if (result === "X") return -1;
      return 0;
    }

    if (isMaximizing) {
      let bestScore = -Infinity;
      for (let i = 0; i < boardState.length; i++) {
        if (!boardState[i]) {
          boardState[i] = "O";
          const score = minimax(boardState, false);
          boardState[i] = null;
          bestScore = Math.max(score, bestScore);
        }
      }
      return bestScore;
    } else {
      let bestScore = Infinity;
      for (let i = 0; i < boardState.length; i++) {
        if (!boardState[i]) {
          boardState[i] = "X";
          const score = minimax(boardState, true);
          boardState[i] = null;
          bestScore = Math.min(score, bestScore);
        }
      }
      return bestScore;
    }
  };

  // AI move with difficulty
  const aiMove = (b) => {
    try {
      // helper to list empty indices
      const emptyIndices = b
        .map((cell, idx) => (cell === null ? idx : null))
        .filter((v) => v !== null);

      if (emptyIndices.length === 0) return;

      // Easy: random
      if (difficulty === "easy") {
        const r = emptyIndices[Math.floor(Math.random() * emptyIndices.length)];
        b[r] = "O";
        return;
      }

      // Medium: partially random (40% random)
      if (difficulty === "medium") {
        if (Math.random() < 0.4) {
          const r =
            emptyIndices[Math.floor(Math.random() * emptyIndices.length)];
          b[r] = "O";
          return;
        }
        // otherwise fall through to smart move
      }

      // Hard (or medium fallback): minimax (best move)
      let bestScore = -Infinity;
      let moveIdx;
      for (let idx of emptyIndices) {
        b[idx] = "O";
        const score = minimax(b, false);
        b[idx] = null;
        if (score > bestScore) {
          bestScore = score;
          moveIdx = idx;
        }
      }
      if (moveIdx !== undefined) b[moveIdx] = "O";
    } catch (err) {
      console.error("AI error:", err);
      setError("AI failed — please restart the game.");
    }
  };

  const handleClick = (index) => {
    // validation
    if (!gameStarted) {
      setError("Click START to begin the game.");
      autoClearError();
      return;
    }
    if (!isPlayerTurn) {
      setError("Wait for AI to play.");
      autoClearError();
      return;
    }
    if (winner) {
      setError("Round finished. Click Restart to play again.");
      autoClearError();
      return;
    }
    if (board[index]) {
      setError("Invalid move: cell already used.");
      autoClearError();
      return;
    }

    const newBoard = [...board];
    newBoard[index] = "X";
    setBoard(newBoard);
    setIsPlayerTurn(false);
    setError(null);
  };

  // clear error after 2.5s
  const autoClearError = () => {
    setTimeout(() => setError(null), 2500);
  };

  useEffect(() => {
    if (!gameStarted) return;

    // check winner first (in case player's move ended game)
    const currentWinner = checkWinner(board);
    if (currentWinner) {
      setWinner(currentWinner);
      setWinningLine(currentWinner === "Draw" ? [] : getWinningLine(board));
      return;
    }

    if (!isPlayerTurn) {
      // simulate AI thinking delay
      const t = setTimeout(() => {
        const newBoard = [...board];
        aiMove(newBoard);
        setBoard(newBoard);
        setIsPlayerTurn(true);

        const postWinner = checkWinner(newBoard);
        if (postWinner) {
          setWinner(postWinner);
          setWinningLine(postWinner === "Draw" ? [] : getWinningLine(newBoard));
        }
      }, 350 + Math.random() * 250); // 350-600ms
      return () => clearTimeout(t);
    }
  }, [board, isPlayerTurn, gameStarted]); // eslint-disable-line

  const resetGame = () => {
    setBoard(emptyBoard);
    setWinner(null);
    setIsPlayerTurn(true);
    setGameStarted(false);
    setWinningLine([]);
    setError(null);
  };

  // small helpers for UI
  const cellIsWinning = (idx) => winningLine.includes(idx);
  const difficultyIcon = (d) => {
    if (d === "easy") return <Smile size={16} />;
    if (d === "hard") return <BrainCircuit size={16} />;
    return <Gauge size={16} />; // medium
  };

  return (
    <section className="mt-12 w-full px-4">
      <div
        className="w-full max-w-6xl mx-auto
    bg-white/80 dark:bg-gray-900/70 backdrop-blur-md 
    rounded-2xl shadow-lg 
    p-5 sm:p-6 md:p-8 lg:p-10 
    flex flex-col lg:flex-row 
    items-center lg:items-start 
    gap-10 
    lg:justify-between"
      >
        <div className="flex-1 text-left">
          <div className="flex items-center gap-3 mb-2">
            <Gamepad className="text-sky-600 dark:text-sky-400" size={18} />
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">
              Featured
            </p>
          </div>

          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Mini Game — Tic Tac Toe
          </h3>

          <p className="text-gray-600 dark:text-gray-400 text-sm md:text-base mb-4">
            Choose difficulty and challenge the AI. Medium is default — balanced
            smart/random moves.
          </p>

          <div className="flex flex-col items-center gap-1">
            <div className="inline-flex rounded-lg bg-gray-100 dark:bg-gray-800 p-1 gap-1">
              {["easy", "medium", "hard"].map((d) => {
                const active = difficulty === d;
                return (
                  <button
                    key={d}
                    onClick={() => !gameStarted && setDifficulty(d)}
                    disabled={gameStarted}
                    aria-pressed={active}
                    title={
                      d === "easy"
                        ? "Easy — random AI"
                        : d === "medium"
                        ? "Medium — balanced"
                        : "Hard — optimal AI"
                    }
                    className={`flex items-center gap-2 px-3 py-2 text-sm rounded-md transition
            ${
              active
                ? "bg-black text-white dark:bg-white dark:text-black"
                : "text-gray-700 dark:text-gray-200"
            }
            ${gameStarted ? "opacity-60 cursor-not-allowed" : ""}
          `}
                  >
                    <span className="opacity-90">{difficultyIcon(d)}</span>
                    <span className="capitalize">{d}</span>
                  </button>
                );
              })}
            </div>

            {/* Difficulty Helper Text */}
            <span className="text-xs text-gray-600 dark:text-gray-400 select-none">
              {difficulty === "easy" && "Difficulty: Easy (Casual Play)"}
              {difficulty === "medium" && "Difficulty: Medium (Balanced)"}
              {difficulty === "hard" && "Difficulty: Hard (Strategic AI)"}
            </span>
          </div>


          {error && <div className="mt-3 text-sm text-red-500">{error}</div>}
        </div>

        {/* right: game board */}
        <div className="relative">
          {/* AI thinking overlay */}
          {!isPlayerTurn && gameStarted && !winner && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm z-10 rounded-md">
              <div className="px-4 py-2 bg-white/90 dark:bg-gray-800/90 text-gray-900 dark:text-white rounded-md shadow text-sm font-medium flex items-center gap-2">
                <BrainCircuit
                  size={16}
                  className="text-sky-500 animate-pulse"
                />
                AI Thinking…
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-2 bg-transparent">
            {board.map((cell, idx) => (
              <button
                key={idx}
                onClick={() => handleClick(idx)}
                disabled={!isPlayerTurn || !!cell} 
                aria-label={`Cell ${idx + 1}`}
                className={`w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center border rounded-md
    transition-transform duration-150 focus:outline-none
    ${
      cellIsWinning(idx)
        ? "ring-2 ring-sky-400 dark:ring-sky-300 shadow-md"
        : ""
    }
    ${
      !isPlayerTurn
        ? "opacity-60 cursor-not-allowed" // 👈 visual lock
        : cell
        ? "cursor-not-allowed"
        : "hover:scale-105 active:scale-95"
    }
    bg-white dark:bg-transparent`}
              >
                {/* Show icons instead of letters */}
                {cell === "X" && (
                  <IconX
                    className="text-rose-600 dark:text-rose-400"
                    size={28}
                  />
                )}
                {cell === "O" && (
                  <IconO className="text-sky-600 dark:text-sky-300" size={26} />
                )}
              </button>
            ))}
          </div>

          {/* overlay when not started or round finished */}
          {(!gameStarted || winner) && (
            <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
              <div className="w-full h-full rounded-md bg-gray-50/30 dark:bg-black/30 backdrop-blur-sm flex flex-col items-center justify-center gap-3 p-4">
                <button
                  onClick={() => (winner ? resetGame() : setGameStarted(true))}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-white/90 dark:bg-gray-800/90 text-gray-900 dark:text-white font-semibold shadow"
                >
                  {winner ? <RefreshCw size={16} /> : <Play size={16} />}
                  <span>{winner ? "Restart" : "Start"}</span>
                </button>

                {winner ? (
                  <div className="text-center mt-1">
                    <div className="text-sm font-semibold dark:text-white text-gray-900">
                      {winner === "Draw" ? "It's a Draw!" : `${winner} wins!`}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    Click Start to begin
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AITicTacToe;
